/**
 * App-level auto-reconnect for the active NWC connection.
 *
 * Connects to the active wallet on app startup. Also reconnects when
 * the active connection changes (e.g., after mnemonic restore when
 * Evolu syncs connection records, or when the user switches wallets).
 *
 * Tracks the connected connection ID to avoid redundant reconnects
 * on re-renders while still reacting to meaningful changes.
 *
 * SECURITY: NWC URIs are reconstructed in-memory only. The reconstructed URI
 * string is passed directly to the connect function and never stored in state,
 * logged, or exposed.
 *
 * Tier: 2 (Lightning features, app-level infrastructure).
 *
 * @module lightning/hooks/useNwcAutoReconnect
 */

import { useEffect, useRef } from 'react';
import type { NwcConnectionRecord } from './useNwcConnections';

/** Options for the useNwcAutoReconnect hook. */
export interface UseNwcAutoReconnectOptions {
  /** All stored NWC connections. */
  readonly connections: readonly NwcConnectionRecord[];
  /** Function to connect using an NWC URI. */
  readonly connectFn: (uri: string) => Promise<void>;
}

/**
 * Auto-connect the active NWC wallet on startup and when the active connection changes.
 *
 * Tracks which connection ID is currently connected. When the active connection
 * changes (including appearing for the first time after Evolu sync), triggers
 * a reconnect. Does not re-trigger on re-renders if the active connection
 * hasn't changed.
 *
 * @param options - Connections list and connect function
 */
export function useNwcAutoReconnect({ connections, connectFn }: UseNwcAutoReconnectOptions): void {
  const connectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const active = connections.find((c) => c.isActive);
    if (!active) return;

    // Skip if we're already connected to this exact connection
    if (connectedIdRef.current === active.id) return;

    connectedIdRef.current = active.id;
    void connectFn(active.connectionSecret);
  }, [connections, connectFn]);
}
